import React, { useState } from 'react';
import { Upload, FileText, Copy, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import domainConfig from './config/domainConfig';
import { generateStandardizedPDF, REPORT_SECTIONS } from './utils/pdfGenerator';
import ReportHeader from './components/ReportHeader';

const BaseDownsReportGenerator = () => {
  const [file, setFile] = useState(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const clearAll = () => {
    setFile(null);
    setReport(null);
    setLoading(false);
    setError(null);
  };

  const handleFileUpload = (event) => {
    const uploadedFile = event.target.files[0];
    if (!uploadedFile) return;

    // Security validations
    const maxFileSize = 10 * 1024 * 1024; // 10MB limit
    const allowedTypes = ['text/csv', 'application/vnd.ms-excel'];
    const allowedExtensions = ['.csv'];
    
    if (uploadedFile.size > maxFileSize) {
      setError('File size must be less than 10MB');
      return;
    }

    const fileExtension = uploadedFile.name.toLowerCase().match(/\.[^.]+$/)?.[0];
    if (!allowedExtensions.includes(fileExtension) || !allowedTypes.includes(uploadedFile.type)) {
      if (!uploadedFile.name.toLowerCase().endsWith('.csv')) {
        setError('Please upload a CSV file only');
        return;
      }
    }

    setFile(uploadedFile);
    setError(null);
  };

  const processData = async () => {
    if (!file) return;
    
    setLoading(true);
    setError(null);

    try {
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('csvFile', file);

      // Send file to server for processing using the generic endpoint
      const response = await fetch(`${domainConfig.apiUrl}/api/reports/basedowns`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate report');
      }

      const result = await response.json();
      setReport(result.report);
    } catch (err) {
      setError('Error processing file: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const getTendencyFlag = (percentage) => {
    const pct = parseFloat(percentage);
    if (pct >= 80) return ' ❗';
    if (pct >= 70) return ' ⚠️';
    return '';
  };

  const generateReportText = () => {
    if (!report) return '';

    let text = `🏈 BASE DOWNS OFFENSIVE TENDENCY REPORT\n`;
    text += `Generated on ${new Date().toLocaleDateString()}\n\n`;

    if (report.logos?.clientLogo || report.logos?.companyLogo) {
      text += `📋 REPORT DETAILS\n`;
      if (report.logos?.clientLogo) text += `Client: [Logo included]\n`;
      if (report.logos?.companyLogo) text += `Analysis by: [Company logo included]\n`;
      text += `\n`;
    }

    text += `📊 DATA VERIFICATION\n`;
    text += `- Using file: ${report.filename || 'CSV file'}\n`;
    text += `- Total dataset rows: ${report.totalRows}\n`;
    text += `- Base down rows: ${report.totalPlays}\n`;
    text += `- Verification complete: ${report.totalPlays} total base down plays confirmed\n\n`;

    text += `📊 SUMMARY\n`;
    text += `- Total Base Down Plays: ${report.totalPlays}\n`;
    text += `- Total Runs: ${report.totalRuns} (${report.runPercentage}% of total plays)\n`;
    text += `- Total Passes: ${report.totalPasses} (${report.passPercentage}% of total plays)\n\n`;

    text += `👥 PERSONNEL USAGE ON BASE DOWNS\n`;
    text += `- Total Personnel Groupings: ${report.personnelUsage.length}\n\n`;

    report.personnelUsage.forEach((personnel, index) => {
      text += `${index + 1}. ${personnel.personnel} Personnel: ${personnel.plays} plays (${personnel.percent}%)\n`;
    });
    text += '\n';

    // Detailed Personnel Analysis
    report.personnelAnalysis.forEach((personnel) => {
      text += `${personnel.personnel.toString().toUpperCase()} PERSONNEL (${personnel.plays} plays - ${personnel.totalPercent}% of base downs)\n\n`;
      text += `Total Runs: ${personnel.runs} (${personnel.runPercent}%)${getTendencyFlag(personnel.runPercent)}\n`;
      text += `Total Passes: ${personnel.passes} (${personnel.passPercent}%)${getTendencyFlag(personnel.passPercent)}\n\n`;

      // Top Formations
      if (personnel.formations.length > 0) {
        text += `Top Formations Used:\n\n`;
        
        personnel.formations.forEach((formation, index) => {
          text += `${index + 1}. ${formation.formation}: ${formation.plays} plays\n`;
          text += `   - Runs: ${formation.runs} (${formation.runPercent}%)${getTendencyFlag(formation.runPercent)}\n`;
          text += `   - Passes: ${formation.passes} (${formation.passPercent}%)${getTendencyFlag(formation.passPercent)}\n\n`;
        });
      }

      // Run Concepts
      if (personnel.runConcepts.length > 0) {
        text += `Run Concepts in ${personnel.personnel} Personnel (${personnel.totalRunPlays} total run plays):\n\n`;
        
        personnel.runConcepts.forEach(concept => {
          text += `- ${concept.concept}: ${concept.count} plays (${concept.percent}%)${getTendencyFlag(concept.percent)}\n`;
        });
        text += '\n';
      }

      // Empty Backfield
      text += `Empty Backfield in ${personnel.personnel} Personnel (${personnel.emptyPlays || 0} total plays ran in 'empty'):\n`;
      
      if (!personnel.emptyPlays || personnel.emptyPlays === 0) {
        text += '- N/A\n\n';
      } else {
        text += `- Runs: ${personnel.emptyRuns} (${personnel.emptyRunPercent}%)${getTendencyFlag(personnel.emptyRunPercent)}\n`;
        text += `- Passes: ${personnel.emptyPasses} (${personnel.emptyPassPercent}%)${getTendencyFlag(personnel.emptyPassPercent)}\n\n`;
      }

      // Unbalanced
      text += `Unbalanced in ${personnel.personnel} Personnel (${personnel.unbalancedPlays || 0} total play${personnel.unbalancedPlays !== 1 ? 's' : ''} ran in unbalanced):\n`;
      
      if (!personnel.unbalancedPlays || personnel.unbalancedPlays === 0) {
        text += '- N/A\n\n';
      } else {
        text += `- Runs: ${personnel.unbalancedRuns} (${personnel.unbalancedRunPercent}%)${getTendencyFlag(personnel.unbalancedRunPercent)}\n`;
        text += `- Passes: ${personnel.unbalancedPasses} (${personnel.unbalancedPassPercent}%)${getTendencyFlag(personnel.unbalancedPassPercent)}\n\n`;
      }
    });

    // Other Personnel Groupings
    if (report.otherPersonnel && report.otherPersonnel.length > 0) {
      text += `Other Personnel Groupings\n\n`;
      
      report.otherPersonnel.forEach(personnel => {
        text += `${personnel.personnel} Personnel (${personnel.plays} plays)\n`;
        if (personnel.runs === personnel.plays) {
          text += `- 100% Run (${personnel.runs} of ${personnel.plays} plays)\n\n`;
        } else if (personnel.passes === personnel.plays) {
          text += `- 100% Pass (${personnel.passes} of ${personnel.plays} plays)\n\n`;
        } else {
          text += `- Runs: ${personnel.runs} (${personnel.runPercent}%), Passes: ${personnel.passes} (${personnel.passPercent}%)\n\n`;
        }
      });
    }

    // Critical Defensive Takeaways
    text += `Critical Defensive Takeaways\n\n`;
    
    if (report.extremeTendencies && report.extremeTendencies.length > 0) {
      text += `Extremely Valuable Tendencies (≥80%) ❗\n\n`;
      report.extremeTendencies.forEach(takeaway => {
        text += `- ${takeaway}\n`;
      });
      text += '\n';
    }

    if (report.valuableTendencies && report.valuableTendencies.length > 0) {
      text += `Valuable Tendencies (70-79%) ⚠️\n\n`;
      report.valuableTendencies.forEach(takeaway => {
        text += `- ${takeaway}\n`;
      });
    }

    return text;
  };

  const copyReport = async () => {
    if (!report) return;

    const reportText = generateReportText();
    
    try {
      await navigator.clipboard.writeText(reportText);
      showSuccessMessage('Report copied to clipboard!');
    } catch (err) {
      setError('Failed to copy to clipboard. Please try again.');
    }
  };

  const showSuccessMessage = (message) => {
    const originalError = error;
    setError(null);
    
    const alertDiv = document.createElement('div');
    alertDiv.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #4caf50;
      color: white;
      padding: 15px 20px;
      border-radius: 5px;
      box-shadow: 0 4px 6px rgba(0,0,0,0.1);
      z-index: 10000;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    `;
    alertDiv.textContent = message;
    document.body.appendChild(alertDiv);
    
    setTimeout(() => {
      document.body.removeChild(alertDiv);
      setError(originalError);
    }, 3000);
  };

  const saveReportAsPDF = async () => {
    await generateStandardizedPDF(
      REPORT_SECTIONS.BASE_DOWNS,
      'base-downs-report.pdf',
      setError
    );
  };

  const renderReport = () => {
    if (!report) return null;

    return (
      <Box sx={{ mt: 2, bgcolor: 'white', minHeight: '100vh' }}>
        {/* Header Section - Using Shared Component */}
        <ReportHeader 
          title="BASE DOWNS ANALYSIS"
          logos={report.logos}
          fileName={file?.name}
        />

        {/* Report Content */}
        <Box sx={{ p: 1.5 }}>

          {/* Data Verification Section */}
          <Box sx={{ mb: 2 }} data-section="base-downs-summary-section">
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#000', mb: 1, borderBottom: '2px solid #000', py: 0.5 }}>
              BASE DOWNS SUMMARY (1st and 2nd downs)
            </Typography>
            
            {/* Summary Stats Cards */}
            <Grid container spacing={1} sx={{ mb: 1 }}>
              <Grid item xs={12} md={3}>
                <Box sx={{ 
                  border: '2px solid #000', 
                  p: 1, 
                  textAlign: 'center',
                  bgcolor: '#e3f2fd'
                }}>
                  <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#000', fontSize: '1.8rem' }}>
                    {report.totalPlays}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#000', fontSize: '0.9rem' }}>
                    BASE DOWN PLAYS
                  </Typography>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={3}>
                <Box sx={{ 
                  border: '2px solid #000', 
                  p: 1, 
                  textAlign: 'center',
                  bgcolor: '#f0f8ff'
                }}>
                  <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#000', fontSize: '1.8rem' }}>
                    {report.totalRuns}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#000', fontSize: '0.9rem' }}>
                    RUNS ({report.runPercentage}%)
                  </Typography>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={3}>
                <Box sx={{ 
                  border: '2px solid #000', 
                  p: 1, 
                  textAlign: 'center',
                  bgcolor: '#fff8f0'
                }}>
                  <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#000', fontSize: '1.8rem' }}>
                    {report.totalPasses}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#000', fontSize: '0.9rem' }}>
                    PASSES ({report.passPercentage}%)
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            {/* Personnel Usage Overview */}
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#000', mb: 2 }}>
              Personnel Usage Overview ({report.personnelUsage.length} total groupings)
            </Typography>
            
            <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
              <Table size="small" sx={{ '& .MuiTableCell-root': { padding: '4px 8px' } }}>
                <TableHead>
                  <TableRow >
                    <TableCell>RANK</TableCell>
                    <TableCell>PERSONNEL</TableCell>
                    <TableCell>PLAYS</TableCell>
                    <TableCell>PERCENTAGE</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {report.personnelUsage.map((personnel, index) => (
                    <TableRow key={index}>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>{personnel.personnel} Personnel</TableCell>
                      <TableCell align="center">{personnel.plays}</TableCell>
                      <TableCell align="center">{personnel.percent}%</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {/* Detailed Personnel Analysis */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#000', mb: 2, borderBottom: '2px solid #000', py: 1 }} data-section="base-downs-personnel-section">
              DETAILED PERSONNEL ANALYSIS
            </Typography>
            
            {report.personnelAnalysis.map((personnel, index) => (
              <Box key={index} sx={{ mb: 4 }} data-section={`base-downs-personnel-${index}`}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#000', mb: 2, bgcolor: '#e9ecef', p: 1, border: '1px solid #000' }}>
                  {personnel.personnel} PERSONNEL ({personnel.totalPercent}% of total base downs)
                </Typography>
                
                {/* Personnel Summary */}
                <Grid container spacing={0} sx={{ mb: 2 }}>
                  <Grid item xs={4}>
                    <Box sx={{ 
                      border: '1px solid #000', 
                      p: 2, 
                      textAlign: 'center',
                      bgcolor: '#f8f9fa'
                    }}>
                      <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#000', fontSize: '1.4rem' }}>
                        {personnel.plays}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>
                        TOTAL PLAYS
                      </Typography>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={4}>
                    <Box sx={{ 
                      border: '1px solid #000', 
                      p: 2, 
                      textAlign: 'center',
                      bgcolor: personnel.runPercent >= 70 ? '#ffebee' : '#f8f9fa'
                    }}>
                      <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#000', fontSize: '1.4rem' }}>
                        {personnel.runs}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>
                        RUNS ({personnel.runPercent}%)
                        {personnel.runPercent >= 80 && ' 🚨'}
                        {personnel.runPercent >= 70 && personnel.runPercent < 80 && ' ⚠️'}
                      </Typography>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={4}>
                    <Box sx={{ 
                      border: '1px solid #000', 
                      p: 2, 
                      textAlign: 'center',
                      bgcolor: personnel.passPercent >= 70 ? '#e3f2fd' : '#f8f9fa'
                    }}>
                      <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#000', fontSize: '1.4rem' }}>
                        {personnel.passes}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>
                        PASSES ({personnel.passPercent}%)
                        {personnel.passPercent >= 80 && ' 🚨'}
                        {personnel.passPercent >= 70 && personnel.passPercent < 80 && ' ⚠️'}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>

                {/* Formations Table */}
                {personnel.formations.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#000', mb: 1 }}>
                      Top Formations Used
                    </Typography>
                    <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
                      <Table size="small" sx={{ '& .MuiTableCell-root': { padding: '4px 8px' } }}>
                        <TableHead>
                          <TableRow>
                            <TableCell>FORMATION</TableCell>
                            <TableCell align="center">PLAYS</TableCell>
                            <TableCell align="center">RUNS</TableCell>
                            <TableCell align="center">PASSES</TableCell>
                            <TableCell align="center">RUN %</TableCell>
                            <TableCell align="center">PASS %</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {personnel.formations.map((formation, fIndex) => (
                            <TableRow key={fIndex}>
                              <TableCell>{formation.formation}</TableCell>
                              <TableCell align="center">
                                {formation.plays}
                              </TableCell>
                              <TableCell align="center">
                                {formation.runs}
                              </TableCell>
                              <TableCell align="center">
                                {formation.passes}
                              </TableCell>
                              <TableCell align="center" sx={{ 
                                color: formation.runPercent >= 80 ? '#d42c2cff' : 
                                         formation.runPercent >= 70 ? '#48d42cff' : 'inherit',
                                fontWeight: formation.runPercent >= 70 ? 'bold' : 'normal'
                              }}>
                                {formation.runPercent}%
                                {formation.runPercent >= 80 && ' 🚨'}
                                {formation.runPercent >= 70 && formation.runPercent < 80 && ' ⚠️'}
                              </TableCell>
                              <TableCell align="center" sx={{ 
                                color: formation.passPercent >= 80 ? '#d42c2cff' : 
                                         formation.passPercent >= 70 ? '#48d42cff' : 'inherit',
                                fontWeight: formation.passPercent >= 70 ? 'bold' : 'normal'
                              }}>
                                {formation.passPercent}%
                                {formation.passPercent >= 80 && ' 🚨'}
                                {formation.passPercent >= 70 && formation.passPercent < 80 && ' ⚠️'}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}

                {/* Run Concepts */}
                {personnel.runConcepts.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#000', mb: 1 }}>
                      Run Concepts ({personnel.totalRunPlays} total run plays)
                    </Typography>
                    <Box>
                      {personnel.runConcepts.map((concept, cIndex) => (
                        <Typography 
                          key={cIndex}
                          variant="body1" 
                          component="span"
                          sx={{ 
                            display: 'inline-block',
                            fontWeight: concept.percent >= 70 ? 'bold' : 'normal',
                            color: concept.percent >= 80 ? 'error.main' : 
                                   concept.percent >= 70 ? 'warning.main' : 'primary.main',
                            fontSize: '0.9rem',
                            mr: 2,
                            mb: 1
                          }}
                        >
                          {concept.concept}: {concept.count} ({concept.percent}%){getTendencyFlag(concept.percent)}
                        </Typography>
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Special Situations */}
                <Grid container spacing={2}>
                  {/* Empty Backfield */}
                  <Grid item xs={12} md={6}>
                    <Box sx={{ border: '1px solid #000', p: 2, bgcolor: '#f8f9fa' }}>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                        Empty Backfield
                      </Typography>
                      {!personnel.emptyPlays || personnel.emptyPlays === 0 ? (
                        <Typography variant="body2">N/A</Typography>
                      ) : (
                        <Box>
                          <Typography variant="body2">
                            <strong>Total:</strong> {personnel.emptyPlays} plays
                          </Typography>
                          <Typography variant="body2">
                            <strong>Runs:</strong> {personnel.emptyRuns} ({personnel.emptyRunPercent}%){getTendencyFlag(personnel.emptyRunPercent)}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Passes:</strong> {personnel.emptyPasses} ({personnel.emptyPassPercent}%){getTendencyFlag(personnel.emptyPassPercent)}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Grid>

                  {/* Unbalanced */}
                  <Grid item xs={12} md={6}>
                    <Box sx={{ border: '1px solid #000', p: 2, bgcolor: '#f8f9fa' }}>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                        Unbalanced Formation
                      </Typography>
                      {!personnel.unbalancedPlays || personnel.unbalancedPlays === 0 ? (
                        <Typography variant="body2">N/A</Typography>
                      ) : (
                        <Box>
                          <Typography variant="body2">
                            <strong>Total:</strong> {personnel.unbalancedPlays} play{personnel.unbalancedPlays !== 1 ? 's' : ''}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Runs:</strong> {personnel.unbalancedRuns} ({personnel.unbalancedRunPercent}%){getTendencyFlag(personnel.unbalancedRunPercent)}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Passes:</strong> {personnel.unbalancedPasses} ({personnel.unbalancedPassPercent}%){getTendencyFlag(personnel.unbalancedPassPercent)}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            ))}

            {/* Other Personnel Groupings */}
            {report.otherPersonnel && report.otherPersonnel.length > 0 && (
              <Box sx={{ mb: 4 }} data-section="base-downs-other-personnel-section">
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#000', mb: 2, bgcolor: '#e9ecef', p: 1, border: '1px solid #000' }}>
                  OTHER PERSONNEL GROUPINGS (Less than 5 plays)
                </Typography>
                
                <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
                  <Table size="small" sx={{ '& .MuiTableCell-root': { padding: '4px 8px' } }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>PERSONNEL</TableCell>
                        <TableCell align="center">PLAYS</TableCell>
                        <TableCell align="center">RUNS</TableCell>
                        <TableCell align="center">PASSES</TableCell>
                        <TableCell align="center">TENDENCY</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {report.otherPersonnel.map((personnel, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            {personnel.personnel} Personnel
                          </TableCell>
                          <TableCell align="center">
                            {personnel.plays}
                          </TableCell>
                          <TableCell align="center">
                            {personnel.runs} ({personnel.runPercent}%)
                          </TableCell>
                          <TableCell align="center">
                            {personnel.passes} ({personnel.passPercent}%)
                          </TableCell>
                          <TableCell align="center">
                            {personnel.runs === personnel.plays ? '100% Run' : 
                             personnel.passes === personnel.plays ? '100% Pass' : 
                             'Mixed'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Box>

          {/* Critical Takeaways Section */}
          <Box sx={{ mb: 4, pageBreakBefore: 'always' }} data-section="base-downs-takeaways-section">
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#000', mb: 2, borderBottom: '2px solid #000', py: 1 }}>
              CRITICAL DEFENSIVE TAKEAWAYS
            </Typography>
            
            {report.extremeTendencies && report.extremeTendencies.length > 0 && (
              <Box sx={{ mb: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#d32f2f', mb: 2, bgcolor: '#ffebee', p: 1, border: '1px solid #d32f2f' }}>
                  🚨 EXTREMELY VALUABLE TENDENCIES (≥80%)
                </Typography>
                <Box component="ul" sx={{ 
                  listStyle: 'none', 
                  padding: 0, 
                  margin: 0,
                  '& li': {
                    mb: 0.5,
                    '&:before': {
                      content: '"• "',
                      fontWeight: 'bold'
                    }
                  }
                }}>
                  {report.extremeTendencies.map((tendency, index) => (
                    <li key={index}>
                      <Typography variant="h6" component="span" sx={{ fontWeight: 'bold' }}>
                        {tendency}
                      </Typography>
                    </li>
                  ))}
                </Box>
              </Box>
            )}

            {report.valuableTendencies && report.valuableTendencies.length > 0 && (
              <Box sx={{ mb: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#f57c00', mb: 2, bgcolor: '#fff3e0', p: 1, border: '1px solid #f57c00' }}>
                  ⚠️ VALUABLE TENDENCIES (70-79%)
                </Typography>
                <Box component="ul" sx={{ 
                  listStyle: 'none', 
                  padding: 0, 
                  margin: 0,
                  '& li': {
                    mb: 0.5,
                    '&:before': {
                      content: '"• "',
                      fontWeight: 'bold'
                    }
                  }
                }}>
                  {report.valuableTendencies.map((tendency, index) => (
                    <li key={index}>
                      <Typography variant="h6" component="span" sx={{ fontWeight: 'normal' }}>
                        {tendency}
                      </Typography>
                    </li>
                  ))}
                </Box>
              </Box>
            )}

            {(!report.extremeTendencies || report.extremeTendencies.length === 0) && 
             (!report.valuableTendencies || report.valuableTendencies.length === 0) && (
              <Box sx={{ p: 3, textAlign: 'center', bgcolor: '#f8f9fa', border: '1px solid #ddd' }}>
                <Typography variant="body1" color="text.secondary">
                  No critical tendencies identified (≥70% threshold)
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      {/* Header with back button */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Button
          component={Link}
          to="/reports"
          variant="outlined"
          startIcon={<ArrowLeft size={16} />}
        >
          Back to Reports
        </Button>
      </Box>

      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <FileText size={32} />
        Base Downs Analysis Report
      </Typography>

      <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
        Upload your opponent's 1st and 2nd down CSV data to generate a comprehensive offensive tendency report. 
        Analyzes personnel groupings, formations, run concepts, and critical defensive takeaways.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* File Upload Section */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Upload CSV Data</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Select a CSV file containing your base downs data. The file should include columns: pff_DOWN, 
            pff_OFFPERSONNELBASIC, pff_OFFFORMATIONGROUP, pff_RUNPASS, pff_RUNCONCEPTPRIMARY.
          </Typography>

          <input
            type="file"
            accept=".csv"
            onChange={handleFileUpload}
            style={{ display: 'none' }}
            id="csv-file-upload"
            disabled={loading}
          />
          <label htmlFor="csv-file-upload">
            <Button
              variant="outlined"
              component="span"
              startIcon={<Upload size={16} />}
              disabled={loading}
              sx={{ mb: 2 }}
            >
              Choose CSV File
            </Button>
          </label>

          {file && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Selected file:</strong> {file.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Size:</strong> {(file.size / 1024 / 1024).toFixed(2)} MB
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <Box sx={{ mb: 4, display: 'flex', gap: 2 }}>
        <Button
          variant="contained"
          onClick={processData}
          disabled={!file || loading}
          startIcon={loading ? <CircularProgress size={20} /> : <FileText />}
          size="large"
        >
          {loading ? 'Generating Report...' : 'Generate Base Downs Report'}
        </Button>
        
        <Button
          variant="outlined"
          onClick={clearAll}
          disabled={loading}
        >
          Clear All
        </Button>
      </Box>

      {loading && (
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <CircularProgress size={24} />
              <Typography variant="h6">Processing your data...</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              The server is analyzing your CSV data and generating the base downs report. This may take a few moments.
            </Typography>
          </CardContent>
        </Card>
      )}

      {report && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h5" gutterBottom>Generated Report</Typography>
          
          <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={copyReport}
              startIcon={<Copy />}
            >
              Copy Report
            </Button>
            <Button
              variant="outlined"
              onClick={saveReportAsPDF}
            >
              Save as PDF
            </Button>
          </Box>

          <Box id="base-downs-report-content">
            {renderReport()}
          </Box>
        </Box>
      )}

      {/* Usage Instructions */}
      <Card sx={{ mt: 4, bgcolor: 'background.default' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>How to Use:</Typography>
          <Typography variant="body2" component="div">
            <ol>
              <li><strong>Upload CSV:</strong> Select your base downs CSV file containing pff_DOWN columns with 1s and 2s</li>
              <li><strong>Automatic Analysis:</strong> The tool filters for base downs and analyzes all personnel groupings</li>
              <li><strong>Review Report:</strong> Examine personnel usage, formation tendencies, and run concepts</li>
              <li><strong>Identify Takeaways:</strong> Focus on flagged tendencies (⚠️ = 70%+, ❗ = 80%+)</li>
              <li><strong>Copy Report:</strong> Use the copy button to save the complete report for game planning</li>
            </ol>
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default BaseDownsReportGenerator;